/** @file Searchable list of institutions for tenant selection at login. */
import moment from 'moment';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import * as icons from '@codegrade/icons';
import { Maybe } from '@codegrade/maybe';
import { expr } from '@codegrade/utils';
import CgError from '@/components/Error';
import { Icon } from '@/components/Icon';
import * as iss from '@/components/InfiniteSearchScrollable';
import SearchInput from '@/components/SearchInput';
import * as utils from '@/utils';
import { clsButtonLikeLink, clsInput } from './cls';
import { routeTenantLogin } from './routes';
const InnerList = iss.create('institutions', {
    debounceTime: moment.duration(250, 'ms'),
});
const QUERY_MINIMUM_LENGTH = 3;
const clsStatusText = 'label-large text-neutral-50 dark:text-brand-10 grow flex items-center justify-center';
/** Searchable list of institutions, allowing the user to find and select their tenant. */
export const TenantList = tsx.component({
    name: 'tenant-list',
    props: {
        search: p.ofFunction().required,
    },
    /** @returns The initial state of the tenant list component. */
    data() {
        return {
            query: '',
        };
    },
    computed: {
        /** @returns The searcher for tenants. */
        searcher() {
            return {
                getItems: () => ({ state: 'loaded-all', data: [] }),
                loadItems: async () => { },
                search: async ({ query, nextToken }) => {
                    if (query.length < QUERY_MINIMUM_LENGTH) {
                        return { state: 'ready', data: [] };
                    }
                    return await this.search({ nextToken, query });
                },
            };
        },
    },
    methods: {
        /**
         * Renders the appropriate UI based on the current search state.
         *
         * @param state The current UI state of the infinite scroll searcher.
         * @param items The tenant results currently loaded.
         * @returns The node to display for the given state.
         */
        renderByState(state, items) {
            return expr.match(state, 'tag', {
                loading: utils.emptyVNode,
                searching: () => this.query.length < QUERY_MINIMUM_LENGTH ? (this.renderPromptForLongerQueryString()) : (<div class={clsStatusText}>Searching{utils.ELLIPSIS}</div>),
                ready: () => <div class={clsStatusText}>Search for your institution by name</div>,
                result: () => this.renderSearchResult(items),
                error: s => (<CgError data-cy="search-error" error={s.error.error} doRetry={s.error.retry}/>),
            });
        },
        /** @returns A prompt asking the user to type more characters before searching. */
        renderPromptForLongerQueryString() {
            return <div class={clsStatusText}>Keep typing to search{utils.ELLIPSIS}</div>;
        },
        /**
         * Renders the search results or a "no results" message.
         *
         * @param items The tenant results to render.
         * @returns The rendered results list or a status message.
         */
        renderSearchResult(items) {
            if (this.query.length < QUERY_MINIMUM_LENGTH) {
                return this.renderPromptForLongerQueryString();
            }
            if (items.length === 0) {
                return <div class={clsStatusText}>No institutions found for "{this.query}"</div>;
            }
            return items.map((item, index) => (<router-link key={item.id} data-cy="tenant-link" data-tenant={item.id} class={[
                    clsButtonLikeLink,
                    'min-h-15 label-large',
                    'focus:bg-actionable-5 dark:focus:bg-actionable-50',
                    // Style the first item when the items container does not have focus within
                    // This is to visually hint the first item will be "selected" when pressing enter on the search input.
                    'not-group-focus-within:first:bg-actionable-5',
                    'group',
                ]} to={routeTenantLogin(this.$router, item.id)} nativeOn={{
                    keyup: evt => {
                        tsx.modifiers.up(innerEvent => this.getSiblingSearchResultItem(index, -1).ifJust(el => {
                            innerEvent.preventDefault();
                            innerEvent.stopPropagation();
                            el.focus();
                            el.scrollIntoView({ behavior: 'auto', block: 'nearest' });
                        }))(evt);
                        tsx.modifiers.down(innerEvent => this.getSiblingSearchResultItem(index, 1).ifJust(el => {
                            innerEvent.preventDefault();
                            innerEvent.stopPropagation();
                            el.focus();
                            el.scrollIntoView({ behavior: 'auto', block: 'nearest' });
                        }))(evt);
                    },
                }}>
                    {item.name}
                    <Icon icon={icons.ArrowRight}/>
                </router-link>));
        },
        /** @returns The first search result element, if present. */
        getFirstSearchResultItem() {
            return Maybe.fromNullable(this.$el.querySelector(`[data-tenant]`)).filter(el => el instanceof HTMLElement);
        },
        /**
         * Returns the search result element adjacent to the given index.
         *
         * @param index The index of the current item.
         * @param direction `1` for next, `-1` for previous.
         * @returns The sibling element, if it exists.
         */
        getSiblingSearchResultItem(index, direction) {
            return Maybe.fromNullable(this.$el.querySelectorAll(`[data-tenant]`).item(index + direction)).filter(el => el instanceof HTMLElement);
        },
    },
    /** @returns The rendered tenant search list JSX. */
    render() {
        return (<div data-cy="tenant-list" class={['flex-auto basis-25 flex flex-col', 'bg-white dark:bg-brand-70']}>
                <InnerList class="contents" searcher={this.searcher} query={this.query} scopedSlots={{
                body: ({ items, state, loadMoreButton }) => [
                    <SearchInput autoFocus data-cy="search-tenant" inputClasses={clsInput} query={this.query} resource="institution" onInput={({ query }) => {
                            this.query = query ?? '';
                        }} inputNativeOn={{
                            keyup: (evt) => {
                                tsx.modifiers.enter(() => this.getFirstSearchResultItem().ifJust(el => el.click()))(evt);
                                tsx.modifiers.down(() => this.getFirstSearchResultItem().ifJust(el => el.focus()))(evt);
                            },
                        }}/>,
                    <div data-cy="tenant-list" class="group flex-auto min-h-60 max-h-[65dvh] md:max-h-110 overflow-auto flex flex-col p-1 gap-1">
                                {this.renderByState(state, items)}
                                {loadMoreButton}
                            </div>,
                ],
            }}/>
            </div>);
    },
});
